import React, { useState, useEffect, useCallback } from 'react';
import { customersService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  MapPin,
  Edit2,
  Trash2,
  UserCheck,
} from 'lucide-react';

export const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    cellphone: '',
    telephone: '',
    address1: '',
    address2: '',
  });
  const [errors, setErrors] = useState({});

  const { success, error: toastError } = useToast();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customersService.list({ search });
      if (res && res.data) {
        setCustomers(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar clientes.');
    } finally {
      setLoading(false);
    }
  }, [search, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchCustomers]);

  const openCreateModal = () => {
    setEditingCustomer(null);
    setFormData({
      full_name: '',
      email: '',
      cellphone: '',
      telephone: '',
      address1: '',
      address2: '',
    });
    setErrors({});
    setModalOpen(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      full_name: customer.full_name || '',
      email: customer.email || '',
      cellphone: customer.cellphone || '',
      telephone: customer.telephone || '',
      address1: customer.address1 || '',
      address2: customer.address2 || '',
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      if (editingCustomer) {
        await customersService.update(editingCustomer.id, formData);
        success('Cliente actualizado correctamente.');
      } else {
        await customersService.create(formData);
        success('Cliente creado exitosamente.');
      }
      setModalOpen(false);
      fetchCustomers();
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al guardar cliente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!customerToDelete) return;
    setSubmitting(true);
    try {
      await customersService.delete(customerToDelete.id);
      success('Cliente eliminado correctamente.');
      setDeleteConfirmOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar cliente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Directorio de Clientes
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Gestiona la información de contacto y facturación de tus clientes
          </p>
        </div>

        <Button onClick={openCreateModal} icon={Plus} className="bg-[#08707A] hover:bg-[#065B63]">
          Nuevo Cliente
        </Button>
      </div>

      {/* Search & List Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3 w-full sm:w-80">
            <Input
              placeholder="Buscar por nombre, email o teléfono..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando clientes..." className="py-12" />
          ) : customers.length === 0 ? (
            <EmptyState
              icon={UserCheck}
              title="No hay clientes registrados"
              description={search ? "No se encontraron clientes que coincidan con tu búsqueda." : "Comienza agregando tu primer cliente para emitir facturas y cotizaciones."}
              actionLabel="Agregar Cliente"
              onAction={openCreateModal}
              actionIcon={Plus}
            />
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {customers.map((c) => (
                <div
                  key={c.id}
                  className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                >
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-950/60 text-[#08707A] dark:text-teal-400 border border-teal-100 dark:border-teal-900/60 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {c.full_name ? c.full_name.charAt(0).toUpperCase() : 'C'}
                    </div>

                    <div className="space-y-1">
                      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        {c.full_name}
                      </h3>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                        {c.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            {c.email}
                          </span>
                        )}
                        {(c.cellphone || c.telephone) && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            {c.cellphone || c.telephone}
                          </span>
                        )}
                        {c.address1 && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {c.address1} {c.address2 ? `, ${c.address2}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 self-end sm:self-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Edit2}
                      onClick={() => openEditModal(c)}
                      title="Editar cliente"
                    >
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={() => {
                        setCustomerToDelete(c);
                        setDeleteConfirmOpen(true);
                      }}
                      title="Eliminar cliente"
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title={editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
        subtitle="Completa los datos de facturación del cliente"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Nombre Completo o Empresa"
            name="full_name"
            placeholder="Ej: Constructora San Juan S.A."
            value={formData.full_name}
            onChange={handleFormChange}
            error={errors.full_name?.[0]}
            required
            autoFocus
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Correo Electrónico"
              type="email"
              name="email"
              placeholder="cliente@dominio.com"
              value={formData.email}
              onChange={handleFormChange}
              error={errors.email?.[0]}
            />

            <Input
              label="Celular"
              name="cellphone"
              placeholder="+1 809-555-0123"
              value={formData.cellphone}
              onChange={handleFormChange}
              error={errors.cellphone?.[0]}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Teléfono Fijo"
              name="telephone"
              placeholder="809-555-0199"
              value={formData.telephone}
              onChange={handleFormChange}
              error={errors.telephone?.[0]}
            />

            <Input
              label="Dirección Principal"
              name="address1"
              placeholder="Calle Principal #123"
              value={formData.address1}
              onChange={handleFormChange}
              error={errors.address1?.[0]}
              required
            />
          </div>

          <Input
            label="Dirección Secundaria (Opcional)"
            name="address2"
            placeholder="Apto 4B, Edificio Los Pinos"
            value={formData.address2}
            onChange={handleFormChange}
            error={errors.address2?.[0]}
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              className="bg-[#08707A] hover:bg-[#065B63]"
            >
              {editingCustomer ? 'Guardar Cambios' : 'Crear Cliente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="¿Eliminar cliente?"
        message={`¿Estás seguro de que deseas eliminar a "${customerToDelete?.full_name}"? Si tiene facturas asociadas, la acción será rechazada por seguridad.`}
        confirmText="Eliminar Cliente"
        isLoading={submitting}
      />
    </div>
  );
};
